import {useMutation, useQuery} from "@apollo/client";
import {toast} from "react-hot-toast";
import {Link} from "react-router";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {shortDateTimeUser} from "@/lib/format.tsx";
import {
  CalculatedBillingDataResponse,
  GQL_CALCULATE_BILLING_DATA,
  GQL_PURCHASE_REQUEST,
  PurchaseRequestResponse,
} from "@/lib/queries/billing.ts";
import {BillingPeriod, CalculatedBillingData} from "@/lib/types/billing.ts";
import {CheckTypeDisplay, MonitoringServiceType} from "@/lib/types/checks.ts";
import {QueryError, ResponseStatus} from "@/lib/types/common.ts";
import {AddOnBox} from "@/pages/billing/v1/instantCheckout/AddOnBox.tsx";
import {InstantPopUp} from "@/pages/billing/v1/instantCheckout/InstantPopUp.tsx";

interface PopupAddOnsRequestProps {
  show: boolean;
  onComplete: () => void;
  service: MonitoringServiceType;
  amount: number;
  limitField: string;
  period: BillingPeriod;
  accountLimit: number;
  requestedAt?: string;
}

export const PopupAddOnsRequest = ({
  show,
  service,
  amount,
  period,
  limitField,
  onComplete,
  accountLimit,
  requestedAt,
}: PopupAddOnsRequestProps) => {
  const {user} = useCurrentUserContext();
  const {data, loading: priceLoading} = useQuery<CalculatedBillingDataResponse>(GQL_CALCULATE_BILLING_DATA, {
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
    variables: {
      billingPeriod: period,
      addedItems: [
        {
          limit_field: limitField,
          amount: amount,
        },
      ],
      skipApplicabilityCheck: true,
    },
  });
  const [purchaseRequest, {loading}] = useMutation<PurchaseRequestResponse>(GQL_PURCHASE_REQUEST);
  if (priceLoading) {
    return null;
  }
  const billingResult = data?.calculateBillingData;

  if (billingResult && (billingResult as QueryError)?.status === ResponseStatus.ERROR) {
    console.error("Error calculating billing data:", (billingResult as QueryError).message);
    return null;
  }

  const calculatedData = billingResult as CalculatedBillingData;
  return (
    <InstantPopUp
      show={show}
      onConfirm={async () => {
        const r = await purchaseRequest({
          variables: {
            addedValues: [
              {
                limit_field: limitField,
                amount: amount,
              },
            ],
          },
        });
        if (r.data?.purchaseRequest.success) {
          toast.success("Request sent successfully. The account owner will review your request.");
        }
        onComplete();
      }}
      loading={loading}
      title={`Upgrade to add ${CheckTypeDisplay[service]} Checks`}
      alertCallout={`You have reached the limit of ${accountLimit} ${CheckTypeDisplay[service]} Checks on your current plan `}
      alertChildren={
        <>
          <span>
            If you would like to request additional {CheckTypeDisplay[service]} Checks, please send a request to the
            owner of this account below to upgrade. If you have any questions don't hesitate to &nbsp;
            <Link to="/contact">contact us</Link>
          </span>
          {requestedAt && (
            <div>
              <br />
              Your request has been sent at: {shortDateTimeUser(requestedAt, user!)}
            </div>
          )}
        </>
      }
      onCancel={onComplete}
      btnTitle="Request Upgrade"
    >
      <AddOnBox
        loading={loading}
        amount={amount}
        price={calculatedData.added_cost_full}
        canAdd={false}
        canRemove={false}
      />
    </InstantPopUp>
  );
};
